'use client';

import { DEFAULT_PROFILE_PICTURE } from '@/atoms/user';
import { CreditsModal } from '@/components/profile/CreditsModal';
import { SearchHistoryModal } from '@/components/profile/SearchHistoryModal';
import { useUser } from '@/hooks/useUser';
import { usePrivy } from '@privy-io/react-auth';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { CreditCard, History, Loader2, LogOut } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';

export function ProfileDropdown() {
  const { user: privyUser, logout } = usePrivy();
  const { user, isLoading } = useUser();
  const [openModal, setOpenModal] = useState<null | 'search' | 'credits' | 'settings'>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  if (!privyUser) return null;

  return (
    <>
      <DropdownMenu.Root open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenu.Trigger asChild>
          <button
            className="w-[30px] h-[30px] flex items-center justify-center rounded-full overflow-hidden border-2 border-white hover:border-gray-400 transition-colors focus:outline-none"
            tabIndex={0}
            aria-label="Open profile menu"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
            ) : (
              <Image
                src={user?.profile_picture || DEFAULT_PROFILE_PICTURE}
                alt="Profile"
                width={30}
                height={30}
                className="w-full h-full object-cover rounded-full"
                unoptimized
              />
            )}
          </button>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          sideOffset={8}
          className="z-50 min-w-[180px] rounded-xl bg-gray-900 border border-[rgb(34,50,83)] shadow-lg py-2 px-1"
        >
          <DropdownMenu.Item
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-white hover:bg-gray-800 cursor-pointer transition-colors"
            onSelect={() => {
              setDropdownOpen(false);
              setOpenModal('search');
            }}
          >
            <History size={16} />
            Search History
          </DropdownMenu.Item>
          <DropdownMenu.Item
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-white hover:bg-gray-800 cursor-pointer transition-colors"
            onSelect={() => {
              setDropdownOpen(false);
              setOpenModal('credits');
            }}
          >
            <CreditCard size={16} />
            Credits
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="my-2 h-px bg-gray-800" />
          <DropdownMenu.Item
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-red-400 hover:bg-red-500/20 cursor-pointer transition-colors"
            onSelect={logout}
          >
            <LogOut size={16} />
            Disconnect
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>

      {/* Modals */}
      <SearchHistoryModal
        open={openModal === 'search'}
        onOpenChange={open => (open ? setOpenModal('search') : setOpenModal(null))}
      />
      <CreditsModal
        open={openModal === 'credits'}
        onOpenChange={open => (open ? setOpenModal('credits') : setOpenModal(null))}
      />
    </>
  );
}
